import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';
import * as Sentry from '@sentry/react';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from 'src/redux/store';
import { getTask } from 'src/redux/slices/taskSlice';
import {
    TASK_EXIT_EVENT,
    UNDO_WINDOW_MS,
    popUndo,
    broadcastTaskRestore,
} from './_shared/taskExitAnimation';

// -----------------------------------------------
// TaskUndoSnackbarHost (#sidebar-v2 Phase 3) — non-rendering effect host
// that listens for the same TASK_EXIT_EVENT the V2 list views react to,
// and surfaces a 6-second Undo Snackbar in the bottom-left corner.
//
// On click of Undo:
//   1. ``broadcastTaskRestore`` re-inserts the stashed task object into
//      every mounted V2 list (TaskBoardView / TaskColumnView /
//      LeadTaskList) — AnimatePresence plays the enter animation for the
//      newly-keyed row.
//   2. POST .../checklist/{itemId}/reopen/ flips the just-completed item
//      back to PENDING on the BE. Same endpoint as
//      ``useCompletionApi.postState(PENDING)`` in ChecklistItemRow.
//   3. ``dispatch(getTask)`` refreshes the slideout's task detail if it's
//      still open so the checklist UI shows PENDING again. The Phase 2
//      deferred refetch already landed with the completed state, so this
//      second fetch is the correction.
//
// Safety notes:
//   • ``popUndo`` drains the stash on first read. Multiple hosts can be
//     mounted (e.g. dashboard + lead-detail tab visible in different
//     surfaces during route transitions) and only the first one fires
//     the Snackbar for a given completion.
//   • If the event carries no token (Phase 2 single-arg callers, or a
//     completion without a stash) we no-op silently — keeps Phase 2
//     callsites working unchanged.
//   • ``notistack`` caps simultaneous Snackbars by its ``maxSnack``
//     prop on the global SnackbarProvider; rapid successive completions
//     stack naturally and older ones get queued.
// -----------------------------------------------
export default function TaskUndoSnackbarHost({ practiceId }) {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    useEffect(() => {
        if (!practiceId) return undefined;
        const handler = (e) => {
            const token = e?.detail?.token;
            const taskId = e?.detail?.taskId;
            if (!token || !taskId) return;
            const stashed = popUndo(token);
            if (!stashed) return; // already consumed by another host
            const { taskSnapshot, lastItemId } = stashed;
            const title = taskSnapshot?.title || taskSnapshot?.name || 'Task';

            enqueueSnackbar(`"${title}" marked complete`, {
                variant: 'success',
                autoHideDuration: UNDO_WINDOW_MS,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                action: (key) => (
                    <Button
                        size="small"
                        color="inherit"
                        sx={{ fontWeight: 700, letterSpacing: '0.04em' }}
                        onClick={async () => {
                            closeSnackbar(key);
                            // 1. Optimistic UI restore — re-insert the
                            //    snapshot into list views immediately so
                            //    the user sees the card return without
                            //    waiting on the BE round-trip.
                            broadcastTaskRestore(token, taskSnapshot);
                            // 2. BE reopen — same endpoint pattern as
                            //    useCompletionApi.postState(PENDING) in
                            //    ChecklistItemRow. Silent on failure
                            //    because the UI is already optimistic;
                            //    the next getTask below will reconcile.
                            if (lastItemId) {
                                try {
                                    await callApi(
                                        `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${lastItemId}/reopen/`,
                                        'POST',
                                        {},
                                    );
                                } catch (err) {
                                    // Hotfix (C3): previously silent — the
                                    // optimistic UI restore would show the
                                    // task back, but the next refetch would
                                    // remove it again because the BE never
                                    // flipped it to PENDING. Surface a
                                    // warning so the user knows to refresh,
                                    // and log to Sentry for visibility.
                                    Sentry.captureException(err);
                                    enqueueSnackbar(
                                        "Couldn't undo — task remains completed. Please refresh.",
                                        { variant: 'warning' },
                                    );
                                }
                            }
                            // 3. Refresh slideout task detail (if open)
                            //    so checklist visually re-flips to PENDING.
                            dispatch(getTask(practiceId, taskId));
                            // 4. Notify the rest of the page that a task went
                            //    back to PENDING, so the Tasks badge, Timeline
                            //    badge and Overview next-step refetch too. The
                            //    optimistic restore above only fixes the lists;
                            //    these counts live in separate components that
                            //    listen for lead-tasks-v2-changed.
                            window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'undo-restore' } }));
                        }}
                    >
                        UNDO
                    </Button>
                ),
            });
        };
        window.addEventListener(TASK_EXIT_EVENT, handler);
        return () => window.removeEventListener(TASK_EXIT_EVENT, handler);
    }, [enqueueSnackbar, closeSnackbar, practiceId]);

    return null;
}

TaskUndoSnackbarHost.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
TaskUndoSnackbarHost.defaultProps = {
    practiceId: null,
};
